import { Controller } from "@hotwired/stimulus"

export default class CryptoSelectAssetController extends Controller {
    static targets = ["amountInput", "walletInput", "previewButton", "loadingSpinner", "previewButtonText", "form", "maxButton", "backButton", "addressBookButton", "pasteButton", "amountHidden", "walletAddressHidden", "houdiniTokenIdHidden", "tokenSymbolHidden"]
    static values = { 
        tokenSymbol: String,
        balance: Number,
        houdiniTokenId: String,
        payload: Object,
        minValue: Number,
        selectedWallet: String
    }

    connect() {
        this.prefillFromPayload()
        
        this.clearPayloadFromURL()
        
        this.validateForm()
        
        if (this.hasPreviewButtonTextTarget) {
            this.originalButtonText = this.previewButtonTextTarget.textContent.trim()
        }
        
        if (this.hasAmountInputTarget) {
            this.amountInputTarget.addEventListener("input", this.validateForm.bind(this))
            this.amountInputTarget.addEventListener("change", this.validateForm.bind(this))
        }
        
        if (this.hasWalletInputTarget) {
            this.walletInputTarget.addEventListener("input", this.validateForm.bind(this))
            this.walletInputTarget.addEventListener("change", this.validateForm.bind(this))
        }

        if (this.hasFormTarget) {
            this.handleTurboStream = this.handleTurboStream.bind(this)
            this.formTarget.addEventListener("turbo:submit-start", this.onSubmitStart.bind(this))
            this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd.bind(this))
        }
    }

    clearPayloadFromURL() {
        const url = new URL(globalThis.location.href)
        if (url.searchParams.has("p")) {
            url.searchParams.delete("p")
            globalThis.history.replaceState({}, "", url.toString())
        }
    }

    prefillFromPayload() {
        if (!this.hasPayloadValue || !this.payloadValue) {
            return
        }

        const payload = this.payloadValue
        const minValue = this.hasMinValueValue ? this.minValueValue : 0
        const balance = this.hasBalanceValue ? this.balanceValue : 0

        if (this.hasAmountInputTarget && payload.amount) {
            const amount = Number.parseFloat(payload.amount)
            
            if (!Number.isNaN(amount)) {
                if (amount >= minValue && amount <= balance) {
                    this.amountInputTarget.value = payload.amount
                } else if (amount > balance) {
                    this.amountInputTarget.value = balance.toFixed(2)
                }
            }
        }

        if (this.hasWalletInputTarget && payload.walletAddress) {
            this.walletInputTarget.value = payload.walletAddress
        }
    }

    selectedWalletValueChanged() {
        if (this.selectedWalletValue && this.hasWalletInputTarget) {
            this.walletInputTarget.value = this.selectedWalletValue
            this.validateForm()
            this.selectedWalletValue = ""
        }
    }

    disconnect() {
        if (this.hasFormTarget) {
            this.formTarget.removeEventListener("turbo:submit-start", this.onSubmitStart)
            this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd)
        }
    }

    onSubmitStart(event) {
        this.isLoading = true
    }

    onSubmitEnd(event) {
        if (event.detail.success === false) {
            this.hideLoading()
        }
    }

    handleTurboStream(event) {
        if (this.isLoading && document.contains(this.element)) {
            this.hideLoading()
            this.isLoading = false
        }
    }

    validateForm() {
        let isValid = true

        if (this.hasAmountInputTarget) {
            const amountValue = this.amountInputTarget.value.trim()
            const cleaned = String(amountValue).replace(/[^0-9.]/g, "")
            const amount = Number.parseFloat(cleaned)
            
            if (!amountValue || amountValue === "" || Number.isNaN(amount) || amount <= 0) {
                isValid = false
            } else if (this.hasBalanceValue && amount > this.balanceValue) {
                isValid = false
            }
        } else {
            isValid = false
        }

        if (this.hasWalletInputTarget) {
            const walletAddress = this.walletInputTarget.value.trim()
            
            if (!walletAddress || walletAddress === "") {
                isValid = false
            }
        } else {
            isValid = false
        }

        if (this.hasPreviewButtonTarget) {
            if (isValid) {
                this.previewButtonTarget.classList.remove("opacity-50", "cursor-not-allowed", "pointer-events-none")
                this.previewButtonTarget.classList.add("hover:bg-black/80")
            } else {
                this.previewButtonTarget.classList.add("opacity-50", "cursor-not-allowed", "pointer-events-none")
                this.previewButtonTarget.classList.remove("hover:bg-black/80")
            }
        }
    }

    handlePreviewClick(event) {
        event.preventDefault()
        
        if (this.isLoading) {
            return false
        }
        
        this.clearPayloadFromURL()
        
        if (!this.isFormValid()) {
            return false
        }
        
        let amountValue = ""
        let walletAddressValue = ""

        if (this.hasAmountInputTarget) {
            amountValue = this.amountInputTarget.value.trim()
            amountValue = String(amountValue).replace(/[^0-9.]/g, "")
        }

        if (this.hasWalletInputTarget) {
            walletAddressValue = this.walletInputTarget.value.trim()
        }

        // Update hidden inputs directly via targets
        if (this.hasAmountHiddenTarget) {
            this.amountHiddenTarget.value = amountValue
        }
        if (this.hasWalletAddressHiddenTarget) {
            this.walletAddressHiddenTarget.value = walletAddressValue
        }
        if (this.hasHoudiniTokenIdHiddenTarget) {
            this.houdiniTokenIdHiddenTarget.value = this.houdiniTokenIdValue
        }
        if (this.hasTokenSymbolHiddenTarget) {
            this.tokenSymbolHiddenTarget.value = this.tokenSymbolValue
        }

        if (this.hasFormTarget && !this.formTarget.checkValidity()) {
            this.formTarget.reportValidity()
            return false
        }

        this.showLoading()

        if (this.hasFormTarget) {
            this.formTarget.requestSubmit()
        }
    }

    showLoading() {
        this.isLoading = true
        
        if (this.hasAmountInputTarget) {
            this.amountInputTarget.disabled = true
        }
        
        if (this.hasWalletInputTarget) {
            this.walletInputTarget.disabled = true
        }
        
        if (this.hasLoadingSpinnerTarget) {
            this.loadingSpinnerTarget.classList.remove("hidden")
        }
        
        if (this.hasPreviewButtonTextTarget) {
            this.previewButtonTextTarget.textContent = "Calculating..."
        }
        
        if (this.hasPreviewButtonTarget) {
            this.previewButtonTarget.disabled = true
            this.previewButtonTarget.classList.remove("hover:bg-black/80")
            this.previewButtonTarget.classList.add("opacity-50", "cursor-not-allowed", "pointer-events-none")
        }
        
        // Disable all interactive elements
        this.setDisabled(true)
    }

    hideLoading() {
        this.isLoading = false
        
        if (this.hasPreviewButtonTarget) {
            this.previewButtonTarget.disabled = false
        }
        
        if (this.hasAmountInputTarget) {
            this.amountInputTarget.disabled = false
        }
        
        if (this.hasWalletInputTarget) {
            this.walletInputTarget.disabled = false
        }
        
        if (this.hasLoadingSpinnerTarget) {
            this.loadingSpinnerTarget.classList.add("hidden")
        }
        
        if (this.hasPreviewButtonTextTarget && this.originalButtonText) {
            this.previewButtonTextTarget.textContent = this.originalButtonText
        }
        
        // Re-enable all interactive elements
        this.setDisabled(false)
        
        this.validateForm()
    }

    isFormValid() {
        let isValid = true

        if (this.hasAmountInputTarget) {
            const amountValue = this.amountInputTarget.value.trim()
            const cleaned = String(amountValue).replace(/[^0-9.]/g, "")
            const amount = Number.parseFloat(cleaned)

            if (!amountValue || amountValue === "" || Number.isNaN(amount) || amount <= 0) {
                isValid = false
            } else if (this.hasBalanceValue && amount > this.balanceValue) {
                isValid = false
            }
        } else {
            isValid = false
        }

        if (this.hasWalletInputTarget) {
            const walletAddress = this.walletInputTarget.value.trim()
            if (!walletAddress || walletAddress === "") {
                isValid = false
            }
        } else {
            isValid = false
        }

        return isValid
    }

    setDisabled(disabled) {
        this.setButtonDisabled(this.hasBackButtonTarget, this.backButtonTarget, disabled, true)
        this.setButtonDisabled(this.hasMaxButtonTarget, this.maxButtonTarget, disabled, false)
        this.setButtonDisabled(this.hasAddressBookButtonTarget, this.addressBookButtonTarget, disabled, true)
        this.setButtonDisabled(this.hasPasteButtonTarget, this.pasteButtonTarget, disabled, false)
    }

    setButtonDisabled(hasTarget, target, disabled, usePointerEvents) {
        if (!hasTarget) return
        
        if (usePointerEvents) {
            if (disabled) {
                target.classList.add("pointer-events-none", "opacity-50")
            } else {
                target.classList.remove("pointer-events-none", "opacity-50")
            }
        } else {
            target.disabled = disabled
            if (disabled) {
                target.classList.add("opacity-50")
            } else {
                target.classList.remove("opacity-50")
            }
        }
    }
}